import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { Form, Col } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMARegistroVisitas } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';

const NOME_TEMPLATE_REGISTRO_VISITAS = 'RegistrodeVisitasaEnsaioseInstalacoescomOGMRegulado.rtf';
const MIME_RTF = 'application/rtf';

const arquivoParaBase64 = (arquivo: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = reader.result;
			if (typeof resultado !== 'string') {
				reject(new Error('Falha ao converter arquivo para base64.'));
				return;
			}
			const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
			resolve(base64);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo.'));
		reader.readAsDataURL(arquivo);
	});

const validarArquivoFormularioPreenchido = (arquivo: File): string | null => {
	const nome = (arquivo.name || '').toLowerCase();
	const okExt =
		nome.endsWith('.rtf') ||
		nome.endsWith('.doc') ||
		nome.endsWith('.docx') ||
		nome.endsWith('.pdf');
	const okMime =
		arquivo.type === MIME_RTF ||
		arquivo.type === 'text/rtf' ||
		arquivo.type === 'application/pdf' ||
		arquivo.type === 'application/msword' ||
		arquivo.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
		arquivo.type === '' ||
		arquivo.type === 'application/octet-stream';

	if (!okExt) {
		return 'Selecione um arquivo RTF, DOC, DOCX ou PDF.';
	}
	if (!okMime) {
		return 'Tipo de arquivo não permitido.';
	}
	return null;
};

const obterNomeArquivoContentDisposition = (contentDisposition?: string): string | null => {
	if (!contentDisposition) {
		return null;
	}

	const filenameUtf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (filenameUtf8?.[1]) {
		return decodeURIComponent(filenameUtf8[1].replace(/"/g, ''));
	}

	const filename = contentDisposition.match(/filename="?([^"]+)"?/i);
	return filename?.[1] ?? null;
};

type RegistroVisitasFormValues = CadernoLPMARegistroVisitas & {
	dataVisita?: Date | string | null;
};

interface RegistroVisitasModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMARegistroVisitas) => void;
	initialData?: CadernoLPMARegistroVisitas;
	cadernoLpmaId?: number | null;
}

const parseDateValue = (valor: any): Date | null => {
	if (!valor && valor !== 0) {
		return null;
	}

	if (valor instanceof Date) {
		return Number.isNaN(valor.getTime()) ? null : valor;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const data = new Date(texto);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	if (typeof valor === 'number') {
		const data = new Date(valor);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	return null;
};

const defaultValues: RegistroVisitasFormValues = {
	id: 0,
	cadernoLPMAId: 0,
	nomeVisitante: '',
	motivoVisita: '',
	dataVisita: null,
};

const converterValoresIniciais = (
	dados?: CadernoLPMARegistroVisitas | null,
): RegistroVisitasFormValues => {
	if (!dados) {
		return { ...defaultValues };
	}

	return {
		...defaultValues,
		...dados,
		dataVisita: parseDateValue(dados.dataVisita),
	};
};

const RegistroVisitasModal: React.FC<RegistroVisitasModalProps> = ({
	show,
	onHide,
	onSubmit,
	initialData,
	cadernoLpmaId,
}) => {
	const estaEditando = useMemo(() => Boolean(initialData?.id), [initialData]);
	const [arquivoFormulario, setArquivoFormulario] = useState<File | null>(null);
	const [erroArquivoFormulario, setErroArquivoFormulario] = useState('');
	const [removerAnexoFormulario, setRemoverAnexoFormulario] = useState(false);

	const idRegistroVisitaEdicao = initialData?.id ?? (initialData as { Id?: number } | undefined)?.Id ?? 0;
	const idCadernoParaDownload =
		cadernoLpmaId ??
		initialData?.cadernoLPMAId ??
		(initialData as { CadernoLPMAId?: number } | undefined)?.CadernoLPMAId ??
		0;

	const { control, handleSubmit, reset } = useForm<RegistroVisitasFormValues>({
		defaultValues,
		mode: 'all',
	});

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setArquivoFormulario(null);
			setErroArquivoFormulario('');
			setRemoverAnexoFormulario(false);
			return;
		}

		reset(converterValoresIniciais(initialData));
		setArquivoFormulario(null);
		setErroArquivoFormulario('');
		setRemoverAnexoFormulario(false);
	}, [show, initialData, reset]);

	const baixarTemplateRegistroVisitas = async () => {
		try {
			const resposta = await api.get(
				'api/v1/cadernoLPMA/cadernoLPMA/registro-visitas/modelo/download',
				{ responseType: 'blob' },
			);
			const contentType = resposta.headers?.['content-type'] ?? MIME_RTF;
			const nomeArquivo =
				obterNomeArquivoContentDisposition(resposta.headers?.['content-disposition']) ??
				NOME_TEMPLATE_REGISTRO_VISITAS;
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nomeArquivo;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			window.alert('Modelo não encontrado no Blob.');
		}
	};

	const baixarFormularioPreenchido = async () => {
		if (!idCadernoParaDownload || !idRegistroVisitaEdicao) {
			return;
		}

		try {
			const resposta = await api.get(
				`api/v1/cadernoLPMA/cadernoLPMA/${idCadernoParaDownload}/registro-visitas/${idRegistroVisitaEdicao}/formulario-preenchido/download`,
				{ responseType: 'blob' },
			);
			const nome =
				initialData?.formularioPreenchidoNomeArquivo ??
				`formulario-registro-visita-${idRegistroVisitaEdicao}`;
			const contentType = initialData?.formularioPreenchidoContentType ?? 'application/octet-stream';
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			window.alert('Não foi possível baixar o formulário preenchido.');
		}
	};

	const handleInternalSubmit = handleSubmit(async (dados) => {
		const processado: CadernoLPMARegistroVisitas = {
			...dados,
			id: dados.id ?? initialData?.id ?? 0,
			cadernoLPMAId: dados.cadernoLPMAId ?? initialData?.cadernoLPMAId ?? 0,
			dataVisita: parseDateValue(dados.dataVisita),
			formularioPreenchidoNomeArquivo: initialData?.formularioPreenchidoNomeArquivo ?? null,
			formularioPreenchidoContentType: initialData?.formularioPreenchidoContentType ?? null,
			formularioPreenchidoCaminhoBlob: initialData?.formularioPreenchidoCaminhoBlob ?? null,
		};

		if (removerAnexoFormulario) {
			processado.removerFormularioPreenchidoAnexo = true;
			processado.formularioPreenchidoNomeArquivo = null;
			processado.formularioPreenchidoContentType = null;
			processado.formularioPreenchidoCaminhoBlob = null;
		} else if (arquivoFormulario) {
			processado.formularioPreenchidoConteudoBase64 = await arquivoParaBase64(arquivoFormulario);
			processado.formularioPreenchidoNomeArquivo = arquivoFormulario.name;
			processado.formularioPreenchidoContentType = arquivoFormulario.type || MIME_RTF;
		}

		onSubmit(processado);
	});

	return (
		<Modal
			show={show}
			onHide={onHide}
			title={STRINGS_CADERNOLPMA.REGISTROVISITAS_MODAL_TITLE}
			bodyClassName="caderno-lpma-modal-body"
		>
			<Form onSubmit={handleInternalSubmit} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Nome do Visitante</Form.Label>
						<Controller
							control={control}
							name="nomeVisitante"
							render={({ value, onChange }) => (
								<Form.Control
									placeholder="Insira o nome do visitante"
									value={value ?? ''}
									onChange={(event) => onChange(event.target.value)}
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Motivo da Visita</Form.Label>
						<Controller
							control={control}
							name="motivoVisita"
							render={({ value, onChange }) => (
								<Form.Control
									placeholder="Insira o motivo da visita"
									value={value ?? ''}
									onChange={(event) => onChange(event.target.value)}
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Data da Visita</Form.Label>
						<Controller
							control={control}
							name="dataVisita"
							render={({ value, onChange }) => (
								<DatePicker
									selected={parseDateValue(value)}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Modelo do formulário de registro de visitas</Form.Label>
						<div>
							<Button
								type="button"
								contained={false}
								title="Baixar modelo"
								onClick={() => void baixarTemplateRegistroVisitas()}
							/>
						</div>
						<small className="text-muted d-block mt-1">
							Baixe o modelo do Blob, preencha e envie o arquivo no campo abaixo.
						</small>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Formulário preenchido</Form.Label>
						<Form.Control
							type="file"
							accept=".rtf,.doc,.docx,.pdf,application/rtf,text/rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
							onChange={(e) => {
								const input = e.target as HTMLInputElement;
								const arquivo = input.files?.[0];
								setErroArquivoFormulario('');
								setRemoverAnexoFormulario(false);
								if (!arquivo) {
									setArquivoFormulario(null);
									return;
								}
								const erro = validarArquivoFormularioPreenchido(arquivo);
								if (erro) {
									setErroArquivoFormulario(erro);
									setArquivoFormulario(null);
									input.value = '';
									return;
								}
								setArquivoFormulario(arquivo);
							}}
						/>
						{erroArquivoFormulario ? (
							<small className="text-danger d-block mt-1">{erroArquivoFormulario}</small>
						) : null}
						{arquivoFormulario ? (
							<small className="d-block text-muted mt-1">Selecionado: {arquivoFormulario.name}</small>
						) : null}
						{!arquivoFormulario &&
						initialData?.formularioPreenchidoNomeArquivo &&
						!removerAnexoFormulario ? (
							<div className="mt-2">
								<small className="text-muted">
									Arquivo atual: {initialData.formularioPreenchidoNomeArquivo}
								</small>
								{idCadernoParaDownload > 0 && idRegistroVisitaEdicao > 0 ? (
									<>
										{' '}
										<Button
											type="button"
											contained={false}
											title="Baixar"
											onClick={() => void baixarFormularioPreenchido()}
										/>
									</>
								) : null}
								{' '}
								<Button
									type="button"
									contained={false}
									title="Remover anexo"
									onClick={() => {
										setRemoverAnexoFormulario(true);
										setArquivoFormulario(null);
									}}
								/>
							</div>
						) : null}
					</Form.Group>
				</Form.Row>
				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained type="submit" />
				</div>
			</Form>
		</Modal>
	);
};

export default RegistroVisitasModal;
